/**
 * LeasingLayout
 *
 * Hub layout for the Leasing section. Renders a page heading, sub-navigation
 * tabs (Dashboard · Inbox · Leads · Listings), and an <Outlet> for the active
 * tab's content. Detail pages (/leasing/leads/:id, /leasing/listings/:id) are
 * rendered outside this layout and do not show the tab strip.
 *
 * Route: /leasing (nested parent)
 */

import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { fetchInboxUnrespondedCount, fetchUnreviewedApplicationsCount } from '../../lib/leasing.api';
import { ScrollFadeTabs } from '../../components/ui/ScrollFadeTabs';
import { AgentSettingsLink } from '../../components/agent/AgentSettingsLink';

interface Tab {
  label: string;
  path: string;
  testId: string;
  showBadge?: boolean;
  // What the count means, for the badge's accessible label ("N <badgeUnit>") — a bare
  // numeral next to a tab label carries no meaning to a screen reader on its own.
  badgeUnit?: string;
}

const TABS: Tab[] = [
  { label: 'Dashboard', path: '/leasing/dashboard', testId: 'leasing-tab-dashboard' },
  { label: 'Inbox',     path: '/leasing/inbox',     testId: 'leasing-tab-inbox',    showBadge: true, badgeUnit: 'unresponded messages' },
  // BR-38 — Applications and Leads now carry their own independent badge counts
  // (previously only Inbox did).
  { label: 'Applications',  path: '/leasing/applications',  testId: 'leasing-tab-applications', showBadge: true, badgeUnit: 'applications to review' },
  { label: 'Leads',         path: '/leasing/leads',         testId: 'leasing-tab-leads',         showBadge: true, badgeUnit: 'leads awaiting your reply' },
  { label: 'Listings',      path: '/leasing/listings',      testId: 'leasing-tab-listings' },
];

export function LeasingLayout() {
  const location = useLocation();
  const activeTab: Tab = (TABS.find((tab) => location.pathname.includes(tab.path)) || TABS[0]) as Tab;
  // BR-38 — Inbox and Leads tabs both reflect the same leasingUnrespondedCount fact
  // (BR-7); reusing this query key dedupes the fetch against AppShell's own subscription
  // to the same key.
  const { data } = useQuery({
    queryKey: ['inbox-unresponded-count'],
    queryFn: () => fetchInboxUnrespondedCount(),
    staleTime: 60_000,
    retry: false,
    throwOnError: false,
  });
  const inboxCount = data?.count ?? 0;

  // BR-38 — Applications tab is an independent count (submitted-and-unreviewed
  // applicants), shares its query key with AppShell's own subscription.
  const { data: unreviewedApplicationsData } = useQuery({
    queryKey: ['applications-unreviewed-count'],
    queryFn: () => fetchUnreviewedApplicationsCount(),
    staleTime: 60_000,
    retry: false,
    throwOnError: false,
  });
  const applicationsCount = unreviewedApplicationsData?.count ?? 0;

  // Per-tab badge values — Inbox and Leads share the same fact (BR-7); Applications is
  // independent (BR-38).
  const badgeByTabPath: Record<string, number> = {
    '/leasing/inbox': inboxCount,
    '/leasing/leads': inboxCount,
    '/leasing/applications': applicationsCount,
  };

  return (
    <div>
      {/* Page heading */}
      <div className="px-[var(--page-padding-x)] pt-6 pb-0">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <h1 className="text-xl font-semibold text-foreground">{activeTab.label}</h1>
          <AgentSettingsLink agentKind="leasing" />
        </div>
      </div>

      {/* Sub-navigation tab strip.
          AC-L9: this used to hand-roll `overflow-x-auto` + a static edge fade, but at
          375px the active tab could land fully off-screen with NO auto-scroll to reveal
          it (e.g. landing directly on /leasing/listings — the rightmost tab — showed
          Dashboard..Leads and cut Listings off entirely with no way to tell it was even
          there). ScrollFadeTabs already solves exactly this (fade + scrollIntoView the
          `data-state="active"` element on mount/change) — reusing it instead of debugging
          the hand-rolled version further. */}
      <div className="mt-4">
        <ScrollFadeTabs activeValue={activeTab.path} className="px-[var(--page-padding-x)] border-b border-border">
        <nav
          className="flex gap-0"
          aria-label="Leasing sub-navigation"
          data-testid="leasing-subnav"
        >
        {TABS.map((tab) => {
          const badge = tab.showBadge ? (badgeByTabPath[tab.path] ?? 0) : 0;
          const isActive = tab.path === activeTab.path;
          return (
            <NavLink
              key={tab.path}
              to={tab.path}
              data-testid={tab.testId}
              data-state={isActive ? 'active' : undefined}
              className={({ isActive }) =>
                cn(
                  'relative flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap',
                  'border-b-2 -mb-px transition-colors',
                  isActive
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )
              }
            >
              {tab.label}
              {badge > 0 && (
                <span
                  data-testid={`${tab.testId}-badge`}
                  aria-label={`${badge} ${tab.badgeUnit ?? 'items'}`}
                  className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground"
                >
                  {badge}
                </span>
              )}
            </NavLink>
          );
        })}
        </nav>
        </ScrollFadeTabs>
      </div>

      {/* Active tab content */}
      <Outlet />
    </div>
  );
}
